import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ThirdPartyContactWithStatus } from './types';

export interface UseRevokeContactsParams {
  /**
   * Scopes the revoke. The tRPC procedure requires it — it bounds both the
   * lookup and the update server-side and 404s on a mismatch — and the Apollo
   * branch of the wrapper hook drops it (the legacy action took bare
   * `ContactIds`).
   */
  thirdPartyId: string;
  selectedContacts: ThirdPartyContactWithStatus[];
  revokeContacts: (input: {
    ThirdPartyId: string;
    ContactIds: string[];
  }) => Promise<unknown>;
  addNotification: (notification: {
    type: 'success' | 'error';
    content: string;
  }) => void;
  refetchContacts: () => Promise<unknown>;
  onSuccess?: () => void;
}

export interface UseRevokeContactsResult {
  revocableContacts: ThirdPartyContactWithStatus[];
  handleRevokeAccess: () => Promise<void>;
}

export const filterRevocableContacts = (
  contacts: ThirdPartyContactWithStatus[]
): ThirdPartyContactWithStatus[] => {
  // Auth0 membership is the real portal-access gate. Re-running revoke for a
  // row already labelled Revoked is the recovery path when an earlier restore
  // changed Auth0 but its database write did not commit.
  return contacts;
};

export const useRevokeContacts = ({
  thirdPartyId,
  selectedContacts,
  revokeContacts,
  addNotification,
  refetchContacts,
  onSuccess,
}: UseRevokeContactsParams): UseRevokeContactsResult => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'third_party.contacts',
  });
  const revocableContacts = useMemo(
    () => filterRevocableContacts(selectedContacts),
    [selectedContacts]
  );

  const handleRevokeAccess = useCallback(async () => {
    if (!revocableContacts.length) {
      return;
    }

    try {
      const contactIds = revocableContacts.map((c) => c.Id);
      await revokeContacts({
        ThirdPartyId: thirdPartyId,
        ContactIds: contactIds,
      });

      // All-or-nothing: if we get here, all contacts were successfully revoked
      addNotification({
        type: 'success',
        content:
          revocableContacts.length === 1
            ? t('revoke_access_success')
            : t('revoke_access_success_multiple', {
                count: revocableContacts.length,
              }),
      });

      onSuccess?.();
      await refetchContacts();
    } catch (_error) {
      // All-or-nothing: if any contact fails, none are revoked
      addNotification({
        type: 'error',
        content: t('revoke_access_error'),
      });
    }
  }, [
    thirdPartyId,
    revocableContacts,
    revokeContacts,
    addNotification,
    t,
    refetchContacts,
    onSuccess,
  ]);

  return {
    revocableContacts,
    handleRevokeAccess,
  };
};
